import React, { useState, useEffect } from 'react';
import { Box, Paper, IconButton, Tooltip, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.15;

const ZoomableDiagram = ({
  src,
  alt,
  initialZoom = 1.35,
  onImageError,
}) => {
  const [zoom, setZoom] = useState(initialZoom);

  useEffect(() => {
    setZoom(initialZoom);
  }, [src, initialZoom]);

  const zoomOut = () => {
    setZoom((z) => Math.max(MIN_ZOOM, Number((z - ZOOM_STEP).toFixed(2))));
  };

  const zoomIn = () => {
    setZoom((z) => Math.min(MAX_ZOOM, Number((z + ZOOM_STEP).toFixed(2))));
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 0.75,
        width: 650,
        height: '100%',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          display: 'flex',
          gap: 0.25,
          alignItems: 'center',
          bgcolor: 'rgba(255,255,255,0.92)',
          borderRadius: 1,
          boxShadow: 1,
          px: 0.25,
        }}
      >
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              size="small"
              onClick={zoomOut}
              disabled={zoom <= MIN_ZOOM}
              aria-label="Zoom out"
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center', userSelect: 'none' }}>
          {Math.round(zoom * 100)}
          %
        </Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              size="small"
              onClick={zoomIn}
              disabled={zoom >= MAX_ZOOM}
              aria-label="Zoom in"
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <img
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          objectFit: 'contain',
          objectPosition: 'center center',
          display: 'block',
          transform: `scale(${zoom})`,
          transformOrigin: 'center center',
        }}
        onError={onImageError}
      />
    </Paper>
  );
};

export default ZoomableDiagram;
